import { useCallback, useEffect, useMemo, useState } from 'react';
import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import { useAuthRequestConfig } from './use-auth-request-config';

const apiClient = axios.create({ baseURL: '/api' });

export type RequestConfig<D = any> = AxiosRequestConfig<D>;

export interface Api<Res = any> {
  request: () => Promise<AxiosResponse<Res>>;
  isLoading: boolean;
  response?: AxiosResponse<Res> | null;
  error?: any;
}

export function useApiClient<Res = any, Req = any>(config: RequestConfig<Req>) {
  type CurrentApi = Api<Res>;
  const authConfig = useAuthRequestConfig(config);

  const [isLoading, setIsLoading] = useState<CurrentApi['isLoading']>(false);
  const [response, setResponse] = useState<CurrentApi['response']>(null);
  const [error, setError] = useState<CurrentApi['error']>(null);

  const request: CurrentApi['request'] = useCallback(async () => {
    try {
      setResponse(null);
      setIsLoading(true);
      setError(null);

      const response = await apiClient.request<Res>(authConfig);

      setResponse(response);
      return response;
    } catch (e) {
      setError(e);
      throw e;
    } finally {
      setIsLoading(false);
    }
  }, [authConfig]);

  useEffect(() => {
    setResponse(null);
    setIsLoading(false);
    setError(null);
  }, [request]);

  const api = useMemo(() => ({ request, response, isLoading, error }), [request, response, isLoading, error]);
  return api;
}
